import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Eye, Search, AlertTriangle, RefreshCw, Landmark, X,
  ChevronLeft, ChevronRight, ArrowUpDown, CreditCard, Clock,
  CheckCircle2
} from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import { describirPagosTipo } from '../data/productosVigentes';
import { formatoMoneda } from '../utils/finanzas';
import { creditosAPI, productosFinancierosAPI, describirError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../styles/Pages.css';
import '../styles/DataTable.css';
import '../styles/CarteraCreditos.css';
import { etiquetaProducto, etiquetaFrecuencia } from '../utils/historicos';

const moneda = formatoMoneda;

const MESES_ABR = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

const fechaCorta = (iso) => {
  if (!iso) return '—';
  if (typeof iso === 'string') {
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = match[1];
      const monthIdx = parseInt(match[2], 10) - 1;
      const day = match[3];
      if (monthIdx >= 0 && monthIdx < 12) {
        return `${day} ${MESES_ABR[monthIdx]} ${year}`;
      }
    }
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).toUpperCase();
};

function Creditos() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [datos, setDatos] = useState({ data: [], pagination: null });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  // Filtros y Ordenamiento
  const [busqueda, setBusqueda] = useState('');
  const [universo, setUniverso] = useState('CARTERA_CON_SALDO');
  const [tipoFiltro, setTipoFiltro] = useState('TODOS');
  const [estadoFiltro, setEstadoFiltro] = useState('TODOS');
  const [tramoFiltro, setTramoFiltro] = useState('TODOS');
  const [moraFiltro, setMoraFiltro] = useState('TODOS');
  const [productoFiltro, setProductoFiltro] = useState('');
  const [frecuenciaFiltro, setFrecuenciaFiltro] = useState('TODAS');
  const [ordenar, setOrdenar] = useState('FOLIO_DESC');
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(25);
  const [productos, setProductos] = useState([]);

  // Cargar catálogo de productos para el filtro
  useEffect(() => {
    productosFinancierosAPI.listar({ activo: 'true' })
      .then(res => setProductos(res.data?.data || []))
      .catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const params = {
        page: pagina,
        limit: limite,
        ordenar,
        universo
      };
      if (busqueda.trim()) params.q = busqueda.trim();
      if (tipoFiltro !== 'TODOS') params.tipoCredito = tipoFiltro === 'INDIVIDUAL' ? 'PERSONAL' : tipoFiltro;
      if (estadoFiltro !== 'TODOS') params.estado = estadoFiltro;
      if (tramoFiltro !== 'TODOS') params.tramoAtraso = tramoFiltro;
      if (moraFiltro !== 'TODOS') params.moraFiltro = moraFiltro;
      if (productoFiltro) params.productoId = productoFiltro;
      if (frecuenciaFiltro !== 'TODAS') params.frecuencia = frecuenciaFiltro;

      const { data } = await creditosAPI.listar(params);
      setDatos(data);
    } catch (e) {
      setError(describirError(e));
      setDatos({ data: [], pagination: null });
    } finally {
      setCargando(false);
    }
  }, [busqueda, universo, tipoFiltro, estadoFiltro, tramoFiltro, moraFiltro, productoFiltro, frecuenciaFiltro, ordenar, pagina, limite]);

  useEffect(() => {
    const t = setTimeout(cargar, 250);
    return () => clearTimeout(t);
  }, [cargar]);

  const filas = datos.data || [];
  const paginacion = datos.pagination || { page: 1, limit: 25, total: 0, totalPages: 1 };
  const resumen = datos.resumen;

  const handleResetFiltros = () => {
    setBusqueda('');
    setUniverso('CARTERA_CON_SALDO');
    setTipoFiltro('TODOS');
    setEstadoFiltro('TODOS');
    setTramoFiltro('TODOS');
    setMoraFiltro('TODOS');
    setProductoFiltro('');
    setFrecuenciaFiltro('TODAS');
    setOrdenar('FOLIO_DESC');
    setPagina(1);
  };

  const hayFiltrosActivos = busqueda || universo !== 'CARTERA_CON_SALDO' || tipoFiltro !== 'TODOS' || estadoFiltro !== 'TODOS' || tramoFiltro !== 'TODOS' || moraFiltro !== 'TODOS' || productoFiltro || frecuenciaFiltro !== 'TODAS' || ordenar !== 'FOLIO_DESC';

  return (
    <div className="page-container cartera-page">
      {/* ── KPIs Ejecutivos de Cartera ── */}
      {resumen && (
        <section className="cartera-kpi-grid-5" aria-label="Resumen ejecutivo de cartera">
          <article className="cartera-kpi-card-v2">
            <div className="cartera-kpi-header">
              <span className="kpi-label">Créditos con saldo</span>
              <span className="kpi-icon-badge">
                <CreditCard size={16} strokeWidth={2} />
              </span>
            </div>
            <p className="cartera-kpi-val">{resumen.totalCreditos ?? paginacion.total}</p>
          </article>

          <article className="cartera-kpi-card-v2 is-brand">
            <div className="cartera-kpi-header">
              <span className="kpi-label">Saldo actual</span>
              <span className="kpi-icon-badge">
                <Landmark size={16} strokeWidth={2} />
              </span>
            </div>
            <p className="cartera-kpi-val">{moneda(resumen.saldoActual || 0)}</p>
          </article>

          <article className="cartera-kpi-card-v2">
            <div className="cartera-kpi-header">
              <span className="kpi-label">Saldo vencido</span>
              <span className="kpi-icon-badge" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626' }}>
                <AlertTriangle size={16} strokeWidth={2} />
              </span>
            </div>
            <p className="cartera-kpi-val" style={{ color: Number(resumen.saldoVencido) > 0 ? '#dc2626' : 'inherit' }}>
              {moneda(resumen.saldoVencido || 0)}
            </p>
          </article>

          <article className="cartera-kpi-card-v2">
            <div className="cartera-kpi-header">
              <span className="kpi-label">Saldo por vencer</span>
              <span className="kpi-icon-badge" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#059669' }}>
                <CheckCircle2 size={16} strokeWidth={2} />
              </span>
            </div>
            <p className="cartera-kpi-val">{moneda(resumen.saldoPorVencer || 0)}</p>
          </article>

          <article className="cartera-kpi-card-v2">
            <div className="cartera-kpi-header">
              <span className="kpi-label">Cuota mínima pendiente</span>
              <span className="kpi-icon-badge">
                <Clock size={16} strokeWidth={2} />
              </span>
            </div>
            <p className="cartera-kpi-val">{moneda(resumen.proximoPago || 0)}</p>
            <p className="cartera-kpi-subtitle">
              Suma de la 1ª cuota pendiente de cada crédito
            </p>
          </article>
        </section>
      )}

      {/* ── Toolbar de Control, Búsqueda, Filtros y Ordenamiento ── */}
      <div className="filters-card cartera-toolbar-card">
        <div className="cartera-toolbar-inner">
          {/* Buscador inteligente */}
          <div className="creditos-search-box" style={{ flex: '1 1 260px', maxWidth: '380px' }}>
            <Search size={16} strokeWidth={1.75} className="creditos-search-icon" />
            <input
              id="cartera-busqueda"
              type="search"
              placeholder="Buscar crédito..."
              aria-label="Buscar créditos en cartera"
              value={busqueda}
              onChange={(e) => {
                setBusqueda(e.target.value);
                setPagina(1);
              }}
            />
            {busqueda && (
              <button
                type="button"
                className="search-clear"
                onClick={() => { setBusqueda(''); setPagina(1); }}
                aria-label="Limpiar búsqueda"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Filtro Universo */}
          <div className="filter-group">
            <label htmlFor="filtro-universo" className="filter-label">Universo:</label>
            <select
              id="filtro-universo"
              className="form-select filter-select"
              value={universo}
              onChange={(e) => { setUniverso(e.target.value); setPagina(1); }}
            >
              <option value="CARTERA_CON_SALDO">Cartera con saldo</option>
              <option value="HISTORICO_COMPLETO">Histórico completo</option>
            </select>
          </div>

          {/* Selector Ordenar por */}
          <div className="filter-group">
            <label htmlFor="ordenar-creditos" className="filter-label">
              <ArrowUpDown size={12} style={{ display: 'inline', marginRight: 4 }} />
              Ordenar por:
            </label>
            <select
              id="ordenar-creditos"
              className="form-select filter-select"
              value={ordenar}
              onChange={(e) => { setOrdenar(e.target.value); setPagina(1); }}
            >
              <option value="FOLIO_DESC">Folio más reciente</option>
              <option value="CLIENTE_ASC">Cliente A–Z</option>
              <option value="CLIENTE_DESC">Cliente Z–A</option>
              <option value="SALDO_DESC">Saldo actual mayor a menor</option>
              <option value="VENCIMIENTO_ASC">Próximo vencimiento</option>
              <option value="ATRASO_DESC">Días de atraso mayor a menor</option>
            </select>
          </div>

          {/* Filtro Tipo / Modalidad */}
          <div className="filter-group">
            <label htmlFor="filtro-tipo" className="filter-label">Modalidad:</label>
            <select
              id="filtro-tipo"
              className="form-select filter-select"
              value={tipoFiltro}
              onChange={(e) => { setTipoFiltro(e.target.value); setPagina(1); }}
            >
              <option value="TODOS">Todas</option>
              <option value="INDIVIDUAL">Individual</option>
              <option value="GRUPAL">Grupal</option>
              <option value="COLOCADORA">Colocadora</option>
            </select>
          </div>

          {/* Filtro Estado */}
          <div className="filter-group">
            <label htmlFor="filtro-estado" className="filter-label">Estado:</label>
            <select
              id="filtro-estado"
              className="form-select filter-select"
              value={estadoFiltro}
              onChange={(e) => { setEstadoFiltro(e.target.value); setPagina(1); }}
            >
              <option value="TODOS">Todos</option>
              <option value="ACTIVO">Activos</option>
              <option value="AUTORIZADO">Autorizados</option>
              <option value="BORRADOR">Borradores</option>
              <option value="LIQUIDADO">Liquidados</option>
              <option value="CANCELADO">Cancelados</option>
              <option value="RECHAZADO">Rechazados</option>
            </select>
          </div>

          {/* Filtro Antigüedad / Tramo de Atraso */}
          <div className="filter-group">
            <label htmlFor="filtro-tramo" className="filter-label">Antigüedad / Tramo:</label>
            <select
              id="filtro-tramo"
              className="form-select filter-select"
              value={tramoFiltro}
              onChange={(e) => { setTramoFiltro(e.target.value); setPagina(1); }}
            >
              <option value="TODOS">Todos</option>
              <option value="0_DIAS">0 días</option>
              <option value="1_7">1–7 días</option>
              <option value="8_15">8–15 días</option>
              <option value="16_30">16–30 días</option>
              <option value="0_30">0–30 días</option>
              <option value="31_60">31–60 días</option>
              <option value="61_90">61–90 días</option>
              <option value="91_MAS">91+ días</option>
            </select>
          </div>

          {/* Filtro Mora / Pago */}
          <div className="filter-group">
            <label htmlFor="filtro-mora" className="filter-label">Mora / Pago:</label>
            <select
              id="filtro-mora"
              className="form-select filter-select"
              value={moraFiltro}
              onChange={(e) => { setMoraFiltro(e.target.value); setPagina(1); }}
            >
              <option value="TODOS">Todos</option>
              <option value="CON_MORA">Con mora</option>
              <option value="SIN_MORA">Sin mora</option>
              <option value="VENCIDOS">Cuotas vencidas</option>
              <option value="PROXIMO_A_VENCER">Próximos a vencer (7d)</option>
              <option value="CERRADOS">Pagados / Cerrados</option>
            </select>
          </div>

          {/* Filtro Frecuencia */}
          <div className="filter-group">
            <label htmlFor="filtro-frecuencia" className="filter-label">Frecuencia:</label>
            <select
              id="filtro-frecuencia"
              className="form-select filter-select"
              value={frecuenciaFiltro}
              onChange={(e) => { setFrecuenciaFiltro(e.target.value); setPagina(1); }}
            >
              <option value="TODAS">Todas</option>
              <option value="SEMANAL">Semanal</option>
              <option value="CATORCENAL">Catorcenal</option>
              <option value="QUINCENAL">Quincenal</option>
              <option value="MENSUAL">Mensual</option>
            </select>
          </div>

          {/* Filtro Producto */}
          {productos.length > 0 && (
            <div className="filter-group">
              <label htmlFor="filtro-producto" className="filter-label">Producto:</label>
              <select
                id="filtro-producto"
                className="form-select filter-select"
                value={productoFiltro}
                onChange={(e) => { setProductoFiltro(e.target.value); setPagina(1); }}
              >
                <option value="">Todos los productos</option>
                {productos.map(p => (
                  <option key={p.id} value={p.id}>{p.nombre || p.legacyName || p.codigo}</option>
                ))}
              </select>
            </div>
          )}

          {/* Limpiar Filtros */}
          {hayFiltrosActivos && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleResetFiltros}
              title="Limpiar todos los filtros"
            >
              <RefreshCw size={13} strokeWidth={2} />
              <span>Limpiar</span>
            </button>
          )}

          {/* Botón Nuevo Crédito (RBAC) */}
          {hasPermission('CREDITOS_CREAR') && (
            <button className="btn btn-primary creditos-btn-add" onClick={() => navigate('/creditos/nuevo')}>
              <Plus size={16} strokeWidth={2} />
              <span>Nuevo crédito</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Contenido Principal ── */}
      {error ? (
        <div className="estado-vacio">
          <span className="estado-vacio-icono is-error">
            <AlertTriangle size={20} strokeWidth={1.75} />
          </span>
          <p className="estado-vacio-titulo">No fue posible cargar la cartera de créditos.</p>
          <p className="estado-vacio-texto">{error.mensaje}</p>
          <button className="btn btn-secondary" onClick={cargar}>
            <RefreshCw size={15} strokeWidth={1.75} />
            Reintentar
          </button>
        </div>
      ) : cargando ? (
        <div className="estado-vacio">
          <span className="spinner" style={{ width: 24, height: 24, marginBottom: 12 }} />
          <p className="estado-vacio-texto">Consultando cartera de créditos…</p>
        </div>
      ) : filas.length === 0 ? (
        <div className="estado-vacio">
          <span className="estado-vacio-icono">
            <Landmark size={20} strokeWidth={1.75} />
          </span>
          <p className="estado-vacio-titulo">
            {hayFiltrosActivos ? 'Ningún crédito coincide con los filtros aplicados.' : 'Todavía no hay créditos registrados.'}
          </p>
          {hayFiltrosActivos ? (
            <button className="btn btn-secondary" onClick={handleResetFiltros}>
              Restablecer filtros
            </button>
          ) : hasPermission('CREDITOS_CREAR') && (
            <button className="btn btn-primary" onClick={() => navigate('/creditos/nuevo')}>
              <Plus size={16} strokeWidth={2} />
              Crear el primero
            </button>
          )}
        </div>
      ) : (
        <>
          {/* ── Resumen de Registros ── */}
          <div className="cartera-meta-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            <span>Mostrando <strong>{filas.length}</strong> de <strong>{paginacion.total}</strong> crédito(s)</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>Por página:</span>
              <select
                className="form-select"
                style={{ padding: '2px 8px', fontSize: '0.8125rem', height: '28px' }}
                value={limite}
                onChange={(e) => { setLimite(Number(e.target.value)); setPagina(1); }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {/* ── Tabla Desktop con Scroll Horizontal Aislado y Columnas Optimizadas ── */}
          <div className="data-table-container desktop-only cartera-table-scroll-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '12%' }}>Folio</th>
                  <th style={{ width: '26%' }}>Cliente / Titular</th>
                  <th style={{ width: '11%' }}>Modalidad</th>
                  <th style={{ width: '18%' }}>Producto</th>
                  <th style={{ width: '13%', textAlign: 'right' }}>Saldo Actual</th>
                  <th style={{ width: '9%', textAlign: 'right' }}>Días Atraso</th>
                  <th style={{ width: '11%' }}>Próx. Venc.</th>
                  <th style={{ width: '5%', textAlign: 'right' }}></th>
                </tr>
              </thead>
              <tbody>
                {filas.map((c) => {
                  const tipoReal = c.tipoCreditoCanonico || (c.tipoCredito === 'PERSONAL' ? 'INDIVIDUAL' : c.tipoCredito);
                  const grupoColocadora = c.grupoNombre || c.colocadoraNombre || (c.agrupacion ? c.agrupacion.nombre : null);
                  const diasAtraso = c.diasAtraso != null ? Number(c.diasAtraso) : 0;

                  return (
                    <tr key={c.id} onClick={() => navigate(`/creditos/${c.id}`)} className="fila-clicable">
                      {/* 1. Folio */}
                      <td>
                        <div className="cell-stack">
                          <span className="font-mono font-semibold creditos-folio-text" style={{ color: 'var(--midas-teal, #0f766e)' }}>
                            {c.folio}
                          </span>
                          <StatusBadge value={c.estado} />
                        </div>
                      </td>

                      {/* 2. Cliente */}
                      <td>
                        <div className="cell-stack" style={{ maxWidth: '240px' }}>
                          <span className="cell-primary font-semibold text-truncate" title={c.titular?.nombreCompleto}>
                            {c.titular?.nombreCompleto || '—'}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            {c.titular?.folio && (
                              <span className="cell-secondary font-mono" style={{ fontSize: '0.75rem' }}>{c.titular.folio}</span>
                            )}
                            {grupoColocadora && (
                              <span className="exp-agrupacion-pill" style={{ fontSize: '0.6875rem', padding: '1px 6px' }} title={grupoColocadora}>
                                {grupoColocadora}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* 3. Modalidad */}
                      <td>
                        <span className={`entity-badge entity-badge--${tipoReal.toLowerCase()}`}>
                          {tipoReal}
                        </span>
                      </td>

                      {/* 4. Producto */}
                      <td>
                        <div className="cell-stack" style={{ maxWidth: '180px' }}>
                          <span className="cell-primary text-truncate" title={etiquetaProducto(c)}>
                            {etiquetaProducto(c)}
                          </span>
                          <span className="cell-secondary" style={{ fontSize: '0.75rem' }}>
                            {c.productoReferencial ? etiquetaFrecuencia(c) : (c.frecuencia || (c.producto ? describirPagosTipo(c.producto) : '—'))}
                          </span>
                        </div>
                      </td>

                      {/* 5. Saldo Actual */}
                      <td style={{ textAlign: 'right' }}>
                        <div className="cell-stack" style={{ alignItems: 'flex-end' }}>
                          <span className="font-semibold" style={{ color: Number(c.saldoActual || 0) > 0 ? 'var(--color-success, #16a34a)' : 'var(--text-secondary)' }}>
                            {moneda(c.saldoActual || 0)}
                          </span>
                          {Number(c.saldoMoratorios || 0) > 0 && (
                            <span className="cell-secondary font-mono" style={{ fontSize: '0.75rem' }} title="Saldo actual = saldo contractual + moratorio">
                              incl. mora: {moneda(c.saldoMoratorios)}
                            </span>
                          )}
                          {c.importeProximoPago != null && (
                            <span className="cell-secondary font-mono" style={{ fontSize: '0.75rem' }} title="Importe próximo pago">
                              Pago: {moneda(c.importeProximoPago)}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* 6. Días Atraso */}
                      <td style={{ textAlign: 'right' }}>
                        {diasAtraso > 0 ? (
                          <span className="cell-secondary text-danger font-semibold font-mono">
                            {diasAtraso} d
                          </span>
                        ) : (
                          <span className="cell-muted font-mono">0 d</span>
                        )}
                      </td>

                      {/* 7. Próximo Vencimiento */}
                      <td>
                        <span className="cell-secondary" style={{ fontSize: '0.8125rem' }}>
                          {fechaCorta(c.proximoVencimiento)}
                        </span>
                      </td>

                      {/* 8. Acciones */}
                      <td className="actions-cell text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon"
                          onClick={() => navigate(`/creditos/${c.id}`)}
                          aria-label="Ver ficha integral"
                          title="Ver ficha integral"
                        >
                          <Eye size={16} strokeWidth={1.75} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Cards Mobile / Tablet (< 1100px) ── */}
          <div className="creditos-cards mobile-only">
            {filas.map((c) => {
              const tipoReal = c.tipoCreditoCanonico || (c.tipoCredito === 'PERSONAL' ? 'INDIVIDUAL' : c.tipoCredito);
              const grupoColocadora = c.grupoNombre || c.colocadoraNombre || (c.agrupacion ? c.agrupacion.nombre : null);
              const diasAtraso = c.diasAtraso != null ? Number(c.diasAtraso) : 0;

              return (
                <div key={c.id} className="credito-card" onClick={() => navigate(`/creditos/${c.id}`)}>
                  <div className="credito-card-header">
                    <div className="credito-card-identity">
                      <span className="credito-card-folio font-mono" style={{ color: 'var(--midas-teal, #0f766e)' }}>{c.folio}</span>
                      <span className="credito-card-titular">{c.titular?.nombreCompleto || '—'}</span>
                      {grupoColocadora && (
                        <span className="credito-card-agrupacion">{grupoColocadora}</span>
                      )}
                    </div>
                    <StatusBadge value={c.estado} />
                  </div>

                  <div className="credito-card-body">
                    <div className="credito-card-tags">
                      <span className={`entity-badge entity-badge--${tipoReal.toLowerCase()}`}>
                        {tipoReal}
                      </span>
                      <span className="credito-card-producto-name">
                        {etiquetaProducto(c)}
                      </span>
                    </div>

                    <div className="credito-card-metrics" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                      <div className="credito-metric-item">
                        <span className="credito-metric-label">Saldo Actual</span>
                        <span className="credito-metric-val font-semibold text-teal">{moneda(c.saldoActual || 0)}</span>
                      </div>
                      <div className="credito-metric-item">
                        <span className="credito-metric-label">Próx. Pago</span>
                        <span className="credito-metric-val">{c.importeProximoPago != null ? moneda(c.importeProximoPago) : '—'}</span>
                      </div>
                      <div className="credito-metric-item">
                        <span className="credito-metric-label">Atraso</span>
                        <span className={`credito-metric-val ${diasAtraso > 0 ? 'text-danger font-semibold' : ''}`}>
                          {diasAtraso} d
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="credito-card-footer" onClick={(e) => e.stopPropagation()}>
                    <span className="text-xs text-muted">
                      Vence: {fechaCorta(c.proximoVencimiento)}
                    </span>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => navigate(`/creditos/${c.id}`)}
                    >
                      <Eye size={14} strokeWidth={1.75} />
                      Ficha Integral
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Paginador ── */}
          {paginacion.totalPages > 1 && (
            <div className="pagination-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '1.25rem', padding: '0.5rem 0' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={pagina <= 1}
                onClick={() => setPagina(p => Math.max(1, p - 1))}
              >
                <ChevronLeft size={14} />
                Anterior
              </button>
              <span style={{ fontSize: '0.875rem', fontWeight: '500', padding: '0 8px' }}>
                Página {paginacion.page} de {paginacion.totalPages}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={pagina >= paginacion.totalPages}
                onClick={() => setPagina(p => Math.min(paginacion.totalPages, p + 1))}
              >
                Siguiente
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Creditos;
